import { useId, useMemo, useState } from 'react'
import { useTranslation } from '../../lib/i18n'
import {
  computeCost,
  DEFAULT_MODEL_ID,
  MODEL_PRICING,
  getModel,
} from '../../lib/model-pricing'
import type { ModelPricing } from '../../lib/model-pricing'

/**
 * Fully client-side LLM cost calculator.
 *
 * Pure arithmetic over the shared model-pricing catalog
 * ({@link ../../lib/model-pricing.ts}): pick two models, enter input + output
 * tokens (or words, auto-converted), a monthly call volume, and optional
 * prompt-caching and batch-discount toggles, and see per-call and monthly cost
 * for both models side by side. No network, no secrets, nothing leaves the
 * browser. Mounted by the tool detail page via the `llm-cost-calculator`
 * toolKey. All UI copy is read from the dictionary so it renders natively in
 * EN and DE; numbers are formatted with Intl in the active locale.
 *
 * SSR-safe: the default state is deterministic and identical on server and
 * client (no Date.now/Math.random in render). Stable ids come from useId.
 */

type Unit = 'tokens' | 'words'

// Deterministic, sensible defaults so server and client render identically.
const DEFAULT_INPUT = 2000
const DEFAULT_OUTPUT = 500
const DEFAULT_CALLS = 1000
// Average English word is ~1.33 tokens (the chars/token heuristic inverted).
const TOKENS_PER_WORD = 1.33

function toTokens(value: number, unit: Unit): number {
  if (unit === 'words') return Math.round(value * TOKENS_PER_WORD)
  return Math.round(value)
}

/** Parse a numeric input defensively; never returns NaN or a negative value. */
function parseAmount(raw: string): number {
  const n = Number.parseFloat(raw)
  if (!Number.isFinite(n) || n < 0) return 0
  return n
}

export function LlmCostCalculator() {
  const { dict, locale } = useTranslation()
  const ui = dict.pages.toolWidgets.costCalculator

  const [modelAId, setModelAId] = useState<string>('claude-opus')
  const [modelBId, setModelBId] = useState<string>(DEFAULT_MODEL_ID)
  const [unit, setUnit] = useState<Unit>('tokens')
  const [inputRaw, setInputRaw] = useState(String(DEFAULT_INPUT))
  const [outputRaw, setOutputRaw] = useState(String(DEFAULT_OUTPUT))
  const [callsRaw, setCallsRaw] = useState(String(DEFAULT_CALLS))
  const [promptCaching, setPromptCaching] = useState(false)
  const [batch, setBatch] = useState(false)

  const baseId = useId()

  const currency = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 4,
      }),
    [locale],
  )
  const money = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
      }),
    [locale],
  )
  const number = useMemo(() => new Intl.NumberFormat(locale), [locale])

  const inputTokens = toTokens(parseAmount(inputRaw), unit)
  const outputTokens = toTokens(parseAmount(outputRaw), unit)
  const monthlyCalls = Math.round(parseAmount(callsRaw))

  const usage = { inputTokens, outputTokens, monthlyCalls, promptCaching, batch }

  const modelA = getModel(modelAId)
  const modelB = getModel(modelBId)

  // Pure arithmetic over the shared catalog; cheap enough to run every render,
  // so no memoisation is needed (and it keeps the dependency surface trivial).
  const resultA = modelA ? computeCost(modelA, usage) : null
  const resultB = modelB ? computeCost(modelB, usage) : null

  const cheaper =
    resultA && resultB
      ? resultA.monthlyCost <= resultB.monthlyCost
        ? 'a'
        : 'b'
      : null

  const renderColumn = (
    side: 'a' | 'b',
    model: ModelPricing | undefined,
    result: ReturnType<typeof computeCost> | null,
    value: string,
    onChange: (id: string) => void,
  ) => {
    const selectId = `${baseId}-model-${side}`
    return (
      <div
        className={`tool-cost-card${cheaper === side ? ' is-cheaper' : ''}`}
      >
        {cheaper === side ? (
          <span className="tool-cost-badge">{ui.cheaperBadge}</span>
        ) : null}
        <label className="tool-field" htmlFor={selectId}>
          <span>{side === 'a' ? ui.modelALabel : ui.modelBLabel}</span>
          <select
            id={selectId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          >
            {MODEL_PRICING.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.providerLabel})
              </option>
            ))}
          </select>
        </label>
        {model && result ? (
          <dl className="tool-cost-figures">
            <div className="tool-cost-row tool-cost-row-lead">
              <dt>{ui.perCall}</dt>
              <dd>{currency.format(result.costPerCall)}</dd>
            </div>
            <div className="tool-cost-row tool-cost-total">
              <dt>{ui.perMonth}</dt>
              <dd>{money.format(result.monthlyCost)}</dd>
            </div>
            <div className="tool-cost-row tool-cost-sub">
              <dt>{ui.inputCost}</dt>
              <dd>{currency.format(result.inputCostPerCall)}</dd>
            </div>
            <div className="tool-cost-row tool-cost-sub">
              <dt>{ui.outputCost}</dt>
              <dd>{currency.format(result.outputCostPerCall)}</dd>
            </div>
            <div className="tool-cost-row tool-cost-meta">
              <dt>{ui.rates}</dt>
              <dd>
                {ui.ratePair
                  .replace('{input}', money.format(model.inputPerMillion))
                  .replace('{output}', money.format(model.outputPerMillion))}
              </dd>
            </div>
          </dl>
        ) : null}
      </div>
    )
  }

  return (
    <div className="tool-widget">
      <form className="tool-widget-form" onSubmit={(e) => e.preventDefault()}>
        <div className="tool-field-grid">
          <label className="tool-field" htmlFor={`${baseId}-input`}>
            <span>
              {unit === 'words' ? ui.inputWordsLabel : ui.inputTokensLabel}
            </span>
            <input
              id={`${baseId}-input`}
              type="number"
              inputMode="numeric"
              min={0}
              step={unit === 'words' ? 10 : 100}
              value={inputRaw}
              onChange={(e) => setInputRaw(e.target.value)}
            />
          </label>
          <label className="tool-field" htmlFor={`${baseId}-output`}>
            <span>
              {unit === 'words' ? ui.outputWordsLabel : ui.outputTokensLabel}
            </span>
            <input
              id={`${baseId}-output`}
              type="number"
              inputMode="numeric"
              min={0}
              step={unit === 'words' ? 10 : 50}
              value={outputRaw}
              onChange={(e) => setOutputRaw(e.target.value)}
            />
          </label>
          <label className="tool-field" htmlFor={`${baseId}-calls`}>
            <span>{ui.callsLabel}</span>
            <input
              id={`${baseId}-calls`}
              type="number"
              inputMode="numeric"
              min={0}
              step={100}
              value={callsRaw}
              onChange={(e) => setCallsRaw(e.target.value)}
            />
          </label>
        </div>

        <fieldset className="tool-toggle-set">
          <legend>{ui.unitLegend}</legend>
          <div className="tool-segmented" role="radiogroup" aria-label={ui.unitLegend}>
            <button
              type="button"
              role="radio"
              aria-checked={unit === 'tokens'}
              className={`tool-segment${unit === 'tokens' ? ' is-active' : ''}`}
              onClick={() => setUnit('tokens')}
            >
              {ui.unitTokens}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={unit === 'words'}
              className={`tool-segment${unit === 'words' ? ' is-active' : ''}`}
              onClick={() => setUnit('words')}
            >
              {ui.unitWords}
            </button>
          </div>
          {unit === 'words' ? (
            <p className="tool-hint">
              {ui.wordsHint
                .replace('{input}', number.format(inputTokens))
                .replace('{output}', number.format(outputTokens))}
            </p>
          ) : null}
        </fieldset>

        <fieldset className="tool-toggle-set">
          <legend>{ui.optionsLegend}</legend>
          <label className="tool-checkbox">
            <input
              type="checkbox"
              checked={promptCaching}
              onChange={(e) => setPromptCaching(e.target.checked)}
            />
            <span>
              <strong>{ui.cachingLabel}</strong>
              <small>{ui.cachingHint}</small>
            </span>
          </label>
          <label className="tool-checkbox">
            <input
              type="checkbox"
              checked={batch}
              onChange={(e) => setBatch(e.target.checked)}
            />
            <span>
              <strong>{ui.batchLabel}</strong>
              <small>{ui.batchHint}</small>
            </span>
          </label>
        </fieldset>
      </form>

      <div className="tool-cost-grid">
        {renderColumn('a', modelA, resultA, modelAId, setModelAId)}
        {renderColumn('b', modelB, resultB, modelBId, setModelBId)}
      </div>

      <p className="tool-disclaimer">{ui.disclaimer}</p>
    </div>
  )
}
